/* calendar-data.js — adapter helpers for the live Appligator calendar API. */
(function () {
  const pad = (n) => String(n).padStart(2, "0");
  const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const fromIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
  const todayIso = () => toIso(new Date());
  const addDays = (iso, n) => { const d = fromIso(iso); d.setDate(d.getDate() + n); return toIso(d); };
  const startOfWeek = (iso) => { const d = fromIso(iso); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return toIso(d); };
  const dow = (iso) => (fromIso(iso).getDay() + 6) % 7;
  const monthKey = (iso) => iso.slice(0, 7);
  const minutesOf = (hhmm) => { const [h, m] = String(hhmm || "00:00").split(":").map(Number); return h * 60 + m; };
  const fmtTime = (hhmm) => {
    const [h, m] = String(hhmm || "00:00").split(":").map(Number);
    return m ? `${h}:${pad(m)}` : `${h}:00`;
  };
  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const WEEKDAYS_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  function fmtDayLabel(iso) {
    const d = fromIso(iso);
    return `${WEEKDAYS_LONG[dow(iso)]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  }
  function fmtDayShort(iso) {
    const d = fromIso(iso);
    return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
  }
  function relativeDay(iso) {
    const t = todayIso();
    if (iso === t) return "Today";
    if (iso === addDays(t, 1)) return "Tomorrow";
    if (iso === addDays(t, -1)) return "Yesterday";
    return null;
  }

  const MEETING_TYPES = ["Kickoff", "Scoping", "Review", "Standup", "Demo", "Workshop", "Handover", "Call"];
  const MEETING_STATUSES = ["unprocessed", "Scheduled", "Done", "Cancelled"];
  const MEETING_RESULT_TYPES = ["Action", "Remark", "Decision", "Risk", "Question"];
  const STATUS_TONE = {
    unprocessed: "unprocessed",
    Scheduled: "scheduled",
    Done: "done",
    Cancelled: "cancelled",
    milestoneDue: "milestone-due",
    milestoneOverdue: "milestone-overdue",
    milestoneDone: "milestone-done",
  };

  const state = {
    employees: [],
    customers: [],
    projects: [],
    employeeById: {},
    projectById: {},
    contactsFlat: [],
    contactById: {},
  };

  function fullName(contact) {
    return [contact && contact.name, contact && contact.lastName].filter(Boolean).join(" ");
  }

  function setDb(db) {
    state.employees = (db && db.employees || []).map((employee) => ({
      id: employee.id,
      name: employee.name || "",
      avatarKey: employee.avatarKey || "",
      role: employee.role || "",
      description: employee.description || "",
      type: employee.type || "Human",
      active: employee.active !== false,
    }));
    state.customers = (db && db.customers || []).map((customer) => ({
      ...customer,
      contactPersons: customer.contactPersons || [],
    }));
    state.projects = (db && db.projects || []).map((project) => ({
      id: project.id,
      name: project.name || "",
      kind: project.kind || "",
      status: project.status || "",
      customerIds: project.customerIds || [],
    }));
    state.employeeById = Object.fromEntries(state.employees.map((employee) => [employee.id, employee]));
    state.projectById = Object.fromEntries(state.projects.map((project) => [project.id, project]));
    state.contactsFlat = state.customers.flatMap((customer) => (customer.contactPersons || []).map((contact) => ({
      ...contact,
      customerId: customer.id,
      customerName: customer.name,
      fullName: fullName(contact),
    })));
    state.contactById = Object.fromEntries(state.contactsFlat.map((contact) => [contact.id, contact]));
  }

  function attendeeLabel(a) {
    if (a.type === "employee") {
      const employee = state.employeeById[a.id];
      return employee ? employee.name : "-";
    }
    const contact = state.contactById[a.id];
    return contact ? contact.fullName : "-";
  }

  function attendeeMeta(a) {
    if (a.type === "employee") {
      const employee = state.employeeById[a.id];
      return employee ? employee.role : "";
    }
    const contact = state.contactById[a.id];
    return contact ? `${contact.role || ""}${contact.role ? " · " : ""}${contact.customerName || ""}` : "";
  }

  function attendeeAvatarKey(a) {
    if (a.type !== "employee") {
      return "";
    }

    const employee = state.employeeById[a.id];
    return employee ? employee.avatarKey : "";
  }

  function newMeeting(overrides) {
    return {
      id: "m" + Math.random().toString(36).slice(2, 9),
      title: "",
      type: "Call",
      status: "Scheduled",
      date: todayIso(),
      start: "10:00",
      end: "11:00",
      projectId: "",
      videoLink: "",
      agenda: "",
      transcript: "",
      results: [],
      attendees: [],
      ...overrides,
    };
  }

  function toMeeting(event) {
    const starts = new Date(event.startsAt);
    const ends = new Date(event.endsAt);
    return {
      id: event.id,
      title: event.title || "",
      type: event.type || "Call",
      status: MEETING_STATUSES.includes(event.status) ? event.status : "Scheduled",
      date: toIso(starts),
      start: `${pad(starts.getHours())}:${pad(starts.getMinutes())}`,
      end: `${pad(ends.getHours())}:${pad(ends.getMinutes())}`,
      projectId: event.projectId || "",
      videoLink: event.videoLink || "",
      agenda: event.description || "",
      transcript: event.transcript || "",
      results: (event.results || []).map((result) => ({
        id: result.id,
        clientId: result.id || "r" + Math.random().toString(36).slice(2, 9),
        type: MEETING_RESULT_TYPES.includes(result.type) ? result.type : "Remark",
        text: result.text || "",
        isRequirement: !!result.isRequirement,
      })),
      attendees: [
        ...(event.employeeIds || []).map((id) => ({ type: "employee", id })),
        ...(event.contactAttendees || []).map((attendee) => ({ type: "contact", id: attendee.contactPersonId })),
      ],
    };
  }

  function milestonesForCalendar(db, scopeProjectId) {
    return (db && db.projects || [])
      .filter((project) => !scopeProjectId || project.id === scopeProjectId)
      .flatMap((project) => (project.milestones || [])
        .filter((milestone) => milestone.dueDate)
        .map((milestone) => {
          const completed = !!milestone.completed;
          const overdue = !completed && milestone.dueDate < todayIso();
          return {
            id: `milestone-${project.id}-${milestone.id}`,
            kind: "milestone",
            milestoneId: milestone.id,
            title: milestone.title || "Milestone due",
            type: overdue ? "Overdue milestone" : "Milestone",
            status: completed ? "milestoneDone" : overdue ? "milestoneOverdue" : "milestoneDue",
            date: milestone.dueDate,
            start: "07:00",
            end: "07:30",
            projectId: project.id,
            projectName: project.name || "",
            completed,
            overdue,
            attendees: [],
            results: [],
          };
        }));
  }

  function localDateTime(date, time) {
    const [year, month, day] = String(date).split("-").map(Number);
    const [hour, minute] = String(time || "00:00").split(":").map(Number);
    return new Date(year, month - 1, day, hour, minute, 0, 0).toISOString();
  }

  function toEventBody(meeting, forcedProjectId, options = {}) {
    const isGuid = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ""));
    const contactAttendees = (meeting.attendees || [])
      .filter((attendee) => attendee.type === "contact")
      .map((attendee) => state.contactById[attendee.id])
      .filter(Boolean)
      .map((contact) => ({ customerId: contact.customerId, contactPersonId: contact.id }));

    const body = {
      projectId: forcedProjectId || meeting.projectId || null,
      title: meeting.title || "",
      type: meeting.type || "Call",
      description: meeting.agenda || "",
      transcript: meeting.transcript || "",
      location: "",
      videoLink: meeting.videoLink || "",
      startsAt: localDateTime(meeting.date, meeting.start),
      endsAt: localDateTime(meeting.date, meeting.end),
      status: meeting.status || "Scheduled",
      employeeIds: (meeting.attendees || []).filter((attendee) => attendee.type === "employee").map((attendee) => attendee.id),
      contactAttendees,
    };

    if (options.includeResults !== false) {
      body.results = (meeting.results || [])
        .filter((result) => (result.text || "").trim())
        .map((result) => ({
          id: isGuid(result.id) ? result.id : null,
          type: result.type || "Action",
          text: result.text.trim(),
          isRequirement: !!result.isRequirement,
        }));
    }

    return body;
  }

  window.CAL = {
    pad, toIso, fromIso, todayIso, addDays, startOfWeek, dow, monthKey, minutesOf, fmtTime,
    WEEKDAYS, WEEKDAYS_LONG, MONTHS, fmtDayLabel, fmtDayShort, relativeDay,
    MEETING_TYPES, MEETING_STATUSES, MEETING_RESULT_TYPES, STATUS_TONE,
    setDb, toMeeting, milestonesForCalendar, toEventBody, attendeeLabel, attendeeMeta, attendeeAvatarKey, newMeeting,
    get employees() { return state.employees; },
    get customers() { return state.customers; },
    get projects() { return state.projects; },
    get employeeById() { return state.employeeById; },
    get projectById() { return state.projectById; },
    get contactsFlat() { return state.contactsFlat; },
    get contactById() { return state.contactById; },
  };
})();
